import * as React from 'react';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

/**
 * LifecycleTracker \u2014 Horizontal progress track with minimal dots.
 *
 * - Completed steps: filled primary dot + check
 * - Active step:     outlined primary dot + solid center
 * - Upcoming steps:  muted gray dot
 *
 * Connector lines are hairline, colored to reflect progress.
 */

export interface LifecycleStep {
  id: string;
  label: React.ReactNode;
  /** Optional tooltip / status text below label */
  caption?: React.ReactNode;
}

export interface LifecycleTrackerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  steps: LifecycleStep[];
  /** 0-based index of the active step */
  activeIndex: number;
  /** Click handler for a step (enables navigation) */
  onStepClick?: (step: LifecycleStep, index: number) => void;
  /** Use compact vertical density (for right-rail panels) */
  compact?: boolean;
}

export const LifecycleTracker = React.forwardRef<
  HTMLDivElement,
  LifecycleTrackerProps
>(({ steps, activeIndex, onStepClick, compact = false, className, ...props }, ref) => {
  if (!steps.length) return null;

  return (
    <div
      ref={ref}
      role="list"
      className={cn(
        'flex items-start w-full',
        compact ? 'gap-0' : 'gap-0',
        className,
      )}
      {...props}
    >
      {steps.map((step, index) => {
        const isCompleted = index < activeIndex;
        const isActive = index === activeIndex;
        const isLast = index === steps.length - 1;
        const clickable = Boolean(onStepClick);

        const dotClass = cn(
          'flex items-center justify-center rounded-full flex-shrink-0 transition-colors',
          compact ? 'h-4 w-4' : 'h-5 w-5',
          isCompleted && 'bg-primary text-primary-foreground',
          isActive &&
            'bg-surface-premium border-2 border-primary [&>span]:bg-primary',
          !isCompleted && !isActive && 'bg-surface-premium-sunken border border-border-hairline',
        );

        const connectorClass = cn(
          'flex-1 h-px mx-1 mt-2',
          isCompleted ? 'bg-primary' : 'bg-border-hairline',
        );

        return (
          <React.Fragment key={step.id}>
            <div
              role="listitem"
              aria-current={isActive ? 'step' : undefined}
              className={cn(
                'flex flex-col items-center min-w-0',
                compact ? 'gap-1' : 'gap-1.5',
                clickable && 'cursor-pointer',
              )}
              onClick={clickable ? () => onStepClick!(step, index) : undefined}
            >
              <div className={dotClass}>
                {isCompleted && (
                  <Check
                    className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'}
                    aria-hidden="true"
                  />
                )}
                {isActive && (
                  <span className="h-1.5 w-1.5 rounded-full" aria-hidden="true" />
                )}
              </div>
              {!compact && (
                <div className="flex flex-col items-center min-w-0 max-w-[120px]">
                  <span
                    className={cn(
                      'text-[11px] leading-tight text-center truncate w-full',
                      isActive
                        ? 'font-semibold text-foreground'
                        : isCompleted
                        ? 'font-medium text-foreground'
                        : 'text-muted-foreground',
                    )}
                  >
                    {step.label}
                  </span>
                  {step.caption && (
                    <span className="text-[11px] text-muted-foreground truncate w-full text-center">
                      {step.caption}
                    </span>
                  )}
                </div>
              )}
            </div>
            {!isLast && <div className={connectorClass} aria-hidden="true" />}
          </React.Fragment>
        );
      })}
    </div>
  );
});
LifecycleTracker.displayName = 'LifecycleTracker';
